import { Checkbox, Chip, MenuItem, TextField } from '@mui/material';
import { Search } from 'lucide-react';
import React from 'react';
import type { Agent, ApiKeyApiScopeInput, ApiScopeCatalogItem, Knowledge } from '../../types';
import { useTranslation } from 'react-i18next';

type RestrictionType = 'agent' | 'knowledge';

const restrictionTypeForScope = (item: ApiScopeCatalogItem): RestrictionType | undefined => {
  const [resourceType] = item.resourceRestrictionTypes;
  return resourceType === 'agent' || resourceType === 'knowledge' ? resourceType : undefined;
};

interface CreateApiKeyScopeCatalogPickerProps {
  groups: Array<[string, ApiScopeCatalogItem[]]>;
  catalogEmpty: boolean;
  agents: Agent[];
  knowledges: Knowledge[];
  selectedCatalogScopes: string[];
  selectedScopeRestrictions: Record<string, ApiKeyApiScopeInput>;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onToggleScope: (scope: string) => void;
  onSetRestriction: (
    scope: string,
    resourceType: RestrictionType,
    resourcePublicId: string
  ) => void;
}

export const CreateApiKeyScopeCatalogPicker: React.FC<CreateApiKeyScopeCatalogPickerProps> = ({
  groups,
  catalogEmpty,
  agents,
  knowledges,
  selectedCatalogScopes,
  selectedScopeRestrictions,
  searchTerm,
  onSearchChange,
  onToggleScope,
  onSetRestriction,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
            {t('apiKeys.modal.resourceScopes')}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
            {t('apiKeys.modal.resourceScopesHelper')}
          </p>
        </div>
        <Chip
          size="small"
          label={t('apiKeys.modal.scopeCount', { count: selectedCatalogScopes.length })}
          color={selectedCatalogScopes.length > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      </div>

      <TextField
        fullWidth
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        size="small"
        placeholder={t('apiKeys.modal.searchScopes', {
          defaultValue: 'Search scopes, capabilities, actions...',
        })}
        InputProps={{
          startAdornment: <Search size={16} className="mr-2 text-gray-400" />,
        }}
        className="!mb-3"
      />

      <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-slate-700">
        {groups.length === 0 ? (
          <div className="p-3 text-sm text-gray-500 dark:text-slate-400">
            {catalogEmpty
              ? t('apiKeys.modal.loadingResources', {
                  defaultValue: 'Loading resources...',
                })
              : t('apiKeys.modal.noScopesFound', {
                  defaultValue: 'No scopes match your search.',
                })}
          </div>
        ) : (
          groups.map(([resourceType, scopes]) => (
            <div
              key={resourceType}
              className="border-t border-gray-100 first:border-t-0 dark:border-slate-700"
            >
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-500 dark:bg-slate-900 dark:text-slate-400">
                {resourceType.replace(/_/g, ' ')}
              </div>
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {scopes.map((item) => {
                  const isSelected = selectedCatalogScopes.includes(item.scope);
                  const restrictionType = restrictionTypeForScope(item);
                  const restriction = selectedScopeRestrictions[item.scope];
                  const restrictionOptions =
                    restrictionType === 'agent'
                      ? agents.map((agent) => ({
                          value: agent.publicId,
                          label: `${agent.name} · ${agent.status || 'draft'} · ${agent.publicId}`,
                        }))
                      : restrictionType === 'knowledge'
                        ? knowledges.map((knowledge) => ({
                            value: knowledge.publicId,
                            label: `${knowledge.name} · ${knowledge.publicId}`,
                          }))
                        : [];

                  return (
                    <div
                      key={item.scope}
                      className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800"
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleScope(item.scope)}
                        size="small"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900 dark:text-slate-100">
                          {item.name}
                        </span>
                        <code className="block break-words text-xs text-blue-700 dark:text-blue-300">
                          {item.scope}
                        </code>
                        <span className="block text-xs text-gray-500 dark:text-slate-400">
                          {item.description}
                        </span>
                        {isSelected && restrictionType && (
                          <TextField
                            select
                            fullWidth
                            value={restriction?.resourcePublicId ?? ''}
                            onChange={(event) =>
                              onSetRestriction(item.scope, restrictionType, event.target.value)
                            }
                            size="small"
                            margin="dense"
                            label={t('apiKeys.modal.restrictToResource', {
                              defaultValue: `Restrict to ${restrictionType}`,
                            })}
                            helperText={t('apiKeys.modal.ownerLevelScopeHelper', {
                              defaultValue:
                                'Leave empty to allow all owned resources for this scope.',
                            })}
                          >
                            <MenuItem value="">
                              {t('apiKeys.modal.allOwnedResources', {
                                defaultValue: 'All owned resources',
                              })}
                            </MenuItem>
                            {restrictionOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </span>
                      {item.source === 'capability' && (
                        <Chip size="small" variant="outlined" label="capability" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
